/* Admin Views Styles
   Extracted from AnalyticsView for use across admin dashboard views:
   - AdminDashboardView (Usage Metrics)
   - BillingView (Export Transactions, Add Matter)
   - AdminSupportView (Support Inbox)
*/

/* ============================================
   WRAPPER & CONTAINER
   ============================================ */
.adminViewWrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-height: 100%;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  background-color: var(--background);
  padding: 24px;
  box-sizing: border-box;
}

:global(.dark) .adminViewWrapper {
  background-color: var(--background);
}

.settingsContainer {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

/* Top row for side-by-side sections (Export + Add Matter) */
.settingsTopRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch; /* Both cards same height */
}

@media screen and (max-width: 900px) {
  .settingsTopRow {
    grid-template-columns: 1fr;
  }
}

/* ============================================
   COLLAPSIBLE SECTION (Export, Add Matter)
   ============================================ */
.headerSection {
  display: flex;
  flex-direction: column;
  background-color: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

.headerSection.expanded {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:global(.dark) .headerSection {
  background-color: var(--card);
  border: 1px solid var(--border);
}

.collapsibleHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.collapsibleHeader:hover .collapseTriangle {
  border-top-color: var(--foreground); /* Darken arrow on hover */
}

.settingsSectionHeader {
	font-size: 1.125rem;
	font-weight: 600;
	color: var(--brand-dark-green);
	margin: 0;
}

:global(.dark) .settingsSectionHeader {
  color: var(--chart-3);
}

.collapseToggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.2s ease;
  transform: rotate(180deg); /* Open = arrow points UP */
}

.collapseToggle.collapsed {
  transform: rotate(0deg); /* Closed = arrow points DOWN */
}

.collapseTriangle {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid color-mix(in oklch, var(--foreground) 50%, transparent);
  transition: border-top-color 0.15s ease;
}

:global(.dark) .collapseTriangle {
  border-top-color: color-mix(in oklch, var(--foreground) 70%, transparent);
}

.collapsibleContent {
  max-height: 2000px;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.2s ease;
  opacity: 1;
  padding: 0 4px; /* Horizontal padding for focus ring visibility */
  margin: 0 -4px; /* Compensate to maintain visual alignment */
}

.collapsibleContent.collapsed {
  max-height: 0;
  opacity: 0;
}

.settingsSectionContent {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  padding: 2px;
}

/* ============================================
   FULL-WIDTH SECTION (Metrics, Support Inbox)
   ============================================ */
.settingsSectionFullWidth {
  background-color: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

:global(.dark) .settingsSectionFullWidth {
  background-color: var(--card);
  border: 1px solid var(--border);
}

/* ============================================
   METRICS SECTION STYLES
   ============================================ */
.metricsHeader {
	font-size: 1.25rem;
	font-weight: 600;
	color: var(--brand-dark-green);
	margin: 0;
}

:global(.dark) .metricsHeader {
  color: var(--chart-3);
}

/* Summary cards grid */
.metricsCard {
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px 20px;
  text-align: center;
  transition: box-shadow 0.15s ease;
}

.metricsCard:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

:global(.dark) .metricsCard {
  background-color: var(--background);
  border: 1px solid var(--border);
}

.metricsCardLabel {
	font-size: 0.75rem;
	font-weight: 500;
	color: var(--muted-foreground);
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
}

.metricsCardValue {
	font-size: 1.75rem;
	font-weight: 700;
	color: var(--foreground);
}

.metricsCardValueGreen {
  color: var(--success);
}

.metricsCardValueRed {
  color: var(--destructive);
}

/* Sub-section header (e.g., "Hours by Attorney" chart title) */
.subSectionHeader {
	font-size: 0.875rem;
	font-weight: 600;
	color: var(--foreground);
	margin: 0 0 12px 0;
}

:global(.dark) .subSectionHeader {
  color: var(--foreground);
}

/* ============================================
   FILTER HEADER ROW
   ============================================ */
.filterHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.filterControls {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ============================================
   DATA TABLE STYLES
   ============================================ */
.dataTable {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.875rem;
}

.dataTable thead tr {
  border-bottom: 2px solid var(--border);
  background-color: color-mix(in oklch, var(--secondary) 30%, transparent);
}

.dataTable th {
  text-align: left;
  padding: 12px 8px;
  font-weight: 500;
  color: var(--foreground);
}

.dataTable th.sortable {
  cursor: pointer;
  user-select: none;
}

.dataTable th.sortable:hover {
  background-color: color-mix(in oklch, var(--accent) 50%, transparent);
}

.dataTable tbody tr {
  border-bottom: 1px solid color-mix(in oklch, var(--border) 50%, transparent);
  transition: background-color 0.1s ease;
}

.dataTable tbody tr:hover {
  background-color: color-mix(in oklch, var(--secondary) 50%, transparent);
}

:global(.dark) .dataTable tbody tr:hover {
  background-color: #737373;
}

.dataTable tbody tr.alternateRow {
  background-color: color-mix(in oklch, var(--secondary) 20%, transparent);
}

.dataTable tbody tr.alternateRow:hover {
  background-color: color-mix(in oklch, var(--secondary) 50%, transparent);
}

:global(.dark) .dataTable tbody tr.alternateRow:hover {
  background-color: #737373;
}

.dataTable td {
  padding: 12px 8px;
}

/* Right-aligned numeric cells */
.dataTable td.numeric,
.dataTable th.numeric {
  text-align: right;
}

/* ============================================
   BADGE/TAG STYLES (for issue types)
   ============================================ */
.badge {
	display: inline-block;
	padding: 2px 8px;
	border-radius: 9999px;
	font-size: 0.75rem;
	font-weight: 500;
}

.badgeBug {
  background-color: hsl(0 84% 95%);
  color: hsl(0 84% 40%);
}

:global(.dark) .badgeBug {
  background-color: hsl(0 84% 20%);
  color: hsl(0 84% 70%);
}

.badgeFeature {
  background-color: hsl(200 84% 95%);
  color: hsl(200 84% 35%);
}

:global(.dark) .badgeFeature {
  background-color: hsl(200 84% 20%);
  color: hsl(200 84% 70%);
}

.badgeQuestion {
  background-color: hsl(270 84% 95%);
  color: hsl(270 84% 35%);
}

:global(.dark) .badgeQuestion {
  background-color: hsl(270 84% 20%);
  color: hsl(270 84% 70%);
}

/* ============================================
   MODAL STYLES
   ============================================ */
.modalOverlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal {
  background-color: var(--background);
  border-radius: var(--radius);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 24px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
}

:global(.dark) .modal {
  background-color: var(--card);
  border: 1px solid var(--border);
}

.modalHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.modalCloseButton {
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: var(--radius);
  transition: color 0.15s ease;
}

.modalCloseButton:hover {
  color: var(--foreground);
}

/* ============================================
   SCREENSHOT STYLES
   ============================================ */
.screenshotThumbnail {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.screenshotThumbnail:hover {
  opacity: 0.8;
}

.screenshotGallery {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.screenshotLarge {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.screenshotLarge:hover {
  opacity: 0.8;
}

/* Enlarged screenshot modal */
.screenshotModalOverlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.8);
}

.screenshotEnlarged {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: var(--radius);
}

/* ============================================
   LOADING & EMPTY STATES
   ============================================ */
.loadingText {
	font-size: 0.875rem;
	color: var(--muted-foreground);
}

.emptyText {
	font-size: 0.875rem;
	color: var(--muted-foreground);
}

/* ============================================
   PREVIEW BOX (for export preview)
   ============================================ */
.previewBox {
  margin-top: 12px;
  padding: 12px;
  background-color: hsl(142 76% 95%);
  border: 1px solid hsl(142 76% 80%);
  border-radius: var(--radius);
}

:global(.dark) .previewBox {
  background-color: hsl(142 76% 15%);
  border-color: hsl(142 76% 30%);
}

.previewText {
  color: hsl(142 76% 30%);
  font-weight: 500;
  text-align: center;
}

:global(.dark) .previewText {
  color: hsl(142 76% 70%);
}

/* ============================================
   FORM ERROR STYLES
   ============================================ */
.fieldError {
	color: var(--destructive);
	font-size: 0.75rem;
	margin-top: 4px;
}

.inputError {
  border-color: var(--destructive) !important;
}

/* ============================================
   HOVER COLOR OVERRIDES (subtle grey instead of blue)
   ============================================ */
.adminViewWrapper :global(.hover\:bg-accent:hover) {
  background-color: var(--muted) !important;
}

:global(.dark) .adminViewWrapper :global(.hover\:bg-accent:hover) {
  background-color: var(--muted) !important;
}

/* Command item hover override (for dropdowns) */
.adminViewWrapper :global([cmdk-item][data-selected="true"]),
.adminViewWrapper :global([cmdk-item]:hover) {
  background-color: var(--muted) !important;
}

:global(.dark) .adminViewWrapper :global([cmdk-item][data-selected="true"]),
:global(.dark) .adminViewWrapper :global([cmdk-item]:hover) {
  background-color: var(--muted) !important;
}

/* ============================================
   ADMIN PRIMARY BUTTON (dark green in light, blue in dark)
   ============================================ */
.adminPrimaryButton {
  background-color: var(--brand-dark-green) !important;
}

.adminPrimaryButton:hover:not(:disabled) {
  background-color: color-mix(in oklch, var(--brand-dark-green) 85%, black) !important;
}

:global(.dark) .adminPrimaryButton {
  background-color: var(--primary) !important;
}

:global(.dark) .adminPrimaryButton:hover:not(:disabled) {
  background-color: color-mix(in oklch, var(--primary) 90%, transparent) !important;
}
